import type { Debt, DebtTotals } from '@familywallet/shared';
import { ChevronDown, ChevronRight, Lock, Users } from 'lucide-react';
import type { TFunction } from 'i18next';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { BaseEstimate } from '../components/Money';
import { PageHeader } from '../components/PageHeader';
import { Button, buttonVariants } from '../components/ui/button';
import { Card, IconSquare, ListGroup, SectionLabel, listRowClass } from '../components/ui/card';
import { InstallmentRow } from '../components/InstallmentRow';
import { useMe } from '../lib/auth';
import { formatDay, todayIn } from '../lib/dates';
import { type DebtSection, outsideSections, type PersonGroup } from '../lib/debt-groups';
import { upcomingInstallments, withoutDuplicatePairs } from '../lib/installments';
import { useDebts } from '../lib/ledger';
import { formatAmount } from '../lib/money';

/** "Badri borrowed" / "Badri lent" for a loan, "Badri bought on credit" for a credit item. */
function whoLabel(debt: Debt, t: TFunction) {
  const owner = debt.owner.displayName;
  if (debt.credit) {
    return debt.direction === 'borrowed'
      ? t('debts.rowBoughtOnCredit', { owner })
      : t('debts.rowSoldOnCredit', { owner });
  }
  return debt.direction === 'borrowed'
    ? t('debts.rowBorrowed', { owner })
    : t('debts.rowLent', { owner });
}

/** One debt in a list: the other side's name, whose debt it is, what is still owed. */
export function DebtRow({ debt, baseCurrency }: { debt: Debt; baseCurrency: string }) {
  const { t } = useTranslation();
  const subtitle = [
    debt.credit?.name || null,
    whoLabel(debt, t),
    debt.settled ? t('debts.settled') : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <Link to={`/debts/${debt.id}`} className={listRowClass}>
      <IconSquare>{debt.person.name.slice(0, 1).toUpperCase()}</IconSquare>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex items-center gap-1.5 truncate text-[15px] font-medium">
          {debt.person.name}
          {debt.isPrivate && (
            <Lock
              aria-label={t('debts.privateDebt')}
              className="size-4 shrink-0 text-muted"
              strokeWidth={2}
            />
          )}
        </span>
        <span className="truncate text-xs text-muted">{subtitle}</span>
      </span>
      <DebtAmount debt={debt} baseCurrency={baseCurrency} />
    </Link>
  );
}

/** What is still owed on one debt, with "≈ $X" underneath for other currencies. */
function DebtAmount({ debt, baseCurrency }: { debt: Debt; baseCurrency: string }) {
  const { i18n } = useTranslation();
  return (
    <span className="flex shrink-0 flex-col items-end">
      <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
        {formatAmount(debt.outstanding, debt.currency, i18n.language)}
      </span>
      {debt.currency !== baseCurrency && debt.outstandingInBase.estimated && (
        <span dir="ltr" className="text-xs text-muted tabular-nums">
          ≈ {formatAmount(debt.outstandingInBase.amount, baseCurrency, i18n.language)}
        </span>
      )}
    </span>
  );
}

/** A total per currency on the end side of a row, "≈ $X" underneath when currencies mix. */
function GroupAmount({ totals }: { totals: DebtTotals }) {
  const { i18n } = useTranslation();
  return (
    <span className="flex shrink-0 flex-col items-end">
      {totals.byCurrency.map((b) => (
        <span key={b.currency} dir="ltr" className="text-[15px] font-semibold tabular-nums">
          {formatAmount(b.balance, b.currency, i18n.language)}
        </span>
      ))}
      {totals.inBase.estimated && (
        <span dir="ltr" className="text-xs text-muted tabular-nums">
          ≈ {formatAmount(totals.inBase.amount, totals.inBase.currency, i18n.language)}
        </span>
      )}
    </span>
  );
}

/**
 * One person in a section. With one item the row opens it; with several it opens in place to
 * list each item, so a specific one can be opened or paid.
 */
function PersonRow({
  group,
  baseCurrency,
  today,
}: {
  group: PersonGroup;
  baseCurrency: string;
  today: string;
}) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const listId = useId();
  const [first] = group.debts;
  if (group.debts.length === 1 && first) {
    return (
      <li>
        <DebtRow debt={first} baseCurrency={baseCurrency} />
      </li>
    );
  }
  const owners = [...new Set(group.debts.map((d) => whoLabel(d, t)))].join(' · ');
  return (
    <li>
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((o) => !o)}
        className={`${listRowClass} w-full text-start`}
      >
        <IconSquare>{group.name.slice(0, 1).toUpperCase()}</IconSquare>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate text-[15px] font-medium">{group.name}</span>
          <span className="truncate text-xs text-muted">
            {t('debts.itemCount', { count: group.debts.length })} · {owners}
          </span>
        </span>
        <GroupAmount totals={group.totals} />
        <ChevronDown
          aria-hidden="true"
          className={`size-5 shrink-0 text-muted ${open ? 'rotate-180' : ''}`}
        />
      </button>
      {open && (
        <ul id={listId} className="flex flex-col divide-y divide-line border-t border-line">
          {group.debts.map((d) => (
            <li key={d.id} className="flex items-center gap-2 pe-4">
              <Link
                to={`/debts/${d.id}`}
                className="flex min-h-14 min-w-0 flex-1 items-center gap-3 py-2 ps-[68px] pe-2 hover:bg-surface-2 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-accent"
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-sm font-medium">
                    {d.credit
                      ? d.credit.name || t('credit.item')
                      : d.note || t(`debts.labels.${d.direction}`)}
                  </span>
                  <span className="truncate text-xs text-muted">
                    {[
                      formatDay(d.startedOn, today, i18n.language, {
                        today: t('activity.today'),
                        yesterday: t('activity.yesterday'),
                      }),
                      whoLabel(d, t),
                    ].join(' · ')}
                  </span>
                </span>
                <DebtAmount debt={d} baseCurrency={baseCurrency} />
              </Link>
              <Link
                to={`/debts/${d.id}/pay`}
                className="group inline-flex min-h-11 shrink-0 items-center focus-visible:outline-none"
              >
                <span className="inline-flex h-8 items-center rounded-full bg-surface-2 px-3 text-sm font-medium group-hover:bg-line group-focus-visible:outline-2 group-focus-visible:outline-accent">
                  {d.direction === 'borrowed' ? t('debts.payShort') : t('debts.collectShort')}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

/** "Borrowed" or "On credit" inside We owe / Owed to us: its total, then one row per person. */
function KindGroup({
  title,
  section,
  baseCurrency,
  today,
}: {
  title: string;
  section: DebtSection;
  baseCurrency: string;
  today: string;
}) {
  return (
    <ListGroup aria-label={title}>
      <li className="flex items-start justify-between gap-3 px-4 py-3">
        <h3 className="text-[15px] font-semibold">{title}</h3>
        <span className="flex flex-col items-end">
          {section.totals.byCurrency.map((b) => (
            <TotalLine key={b.currency} amount={b.balance} currency={b.currency} />
          ))}
          <BaseEstimate total={section.totals.inBase} />
        </span>
      </li>
      {section.people.map((g) => (
        <PersonRow key={g.key} group={g} baseCurrency={baseCurrency} today={today} />
      ))}
    </ListGroup>
  );
}

function TotalLine({ amount, currency }: { amount: string; currency: string }) {
  const { i18n } = useTranslation();
  return (
    <span dir="ltr" className="text-[15px] font-semibold tabular-nums">
      {formatAmount(amount, currency, i18n.language)}
    </span>
  );
}

/** We owe (Borrowed, On credit) or Owed to us (Lent, On credit). */
function DirectionSection({
  title,
  groups,
  baseCurrency,
  today,
}: {
  title: string;
  groups: { title: string; section: DebtSection }[];
  baseCurrency: string;
  today: string;
}) {
  const { t } = useTranslation();
  const shown = groups.filter((g) => g.section.people.length > 0);
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{title}</SectionLabel>
      {shown.length === 0 ? (
        <p className="px-1 text-sm text-muted">{t('debts.noneOpen')}</p>
      ) : (
        shown.map((g) => (
          <KindGroup
            key={g.title}
            title={g.title}
            section={g.section}
            baseCurrency={baseCurrency}
            today={today}
          />
        ))
      )}
    </section>
  );
}

/** Totals per currency (never mixed), with the ≈ base estimate underneath. */
export function TotalsTile({ label, totals }: { label: string; totals: DebtTotals }) {
  const { t, i18n } = useTranslation();
  return (
    <div className="flex flex-col gap-1 rounded-2xl bg-surface-2 p-3">
      <span className="text-[13px] text-muted">{label}</span>
      {totals.byCurrency.length === 0 ? (
        <span className="text-[15px] font-semibold tabular-nums">
          {formatAmount('0', totals.inBase.currency, i18n.language)}
        </span>
      ) : (
        totals.byCurrency.map((b) => (
          <span key={b.currency} dir="ltr" className="text-[15px] font-semibold tabular-nums">
            {formatAmount(b.balance, b.currency, i18n.language)}
          </span>
        ))
      )}
      <BaseEstimate total={totals.inBase} />
      <span className="sr-only">{t('debts.totalsOutside')}</span>
    </div>
  );
}

function Section({
  title,
  hint,
  debts,
  baseCurrency,
}: {
  title: string;
  hint?: string;
  debts: Debt[];
  baseCurrency: string;
}) {
  const { t } = useTranslation();
  return (
    <section className="flex flex-col gap-2">
      <SectionLabel>{title}</SectionLabel>
      {hint && <p className="px-1 text-xs text-muted">{hint}</p>}
      {debts.length === 0 ? (
        <p className="px-1 text-sm text-muted">{t('debts.noneOpen')}</p>
      ) : (
        <ListGroup>
          {debts.map((d) => (
            <li key={d.id}>
              <DebtRow debt={d} baseCurrency={baseCurrency} />
            </li>
          ))}
        </ListGroup>
      )}
    </section>
  );
}

/** "We owe" and "Owed to us" (outside people), debts within the family, then settled ones. */
export function DebtsPage() {
  const { t } = useTranslation();
  const me = useMe();
  const debts = useDebts();
  if (!me.data || debts.isPending) return <p role="status">{t('app.loading')}</p>;
  if (debts.isError) {
    return (
      <Card role="alert" className="flex flex-col gap-3">
        <p>{t('common.loadFailed')}</p>
        <Button variant="secondary" onClick={() => void debts.refetch()}>
          {t('common.retry')}
        </Button>
      </Card>
    );
  }
  const { baseCurrency } = me.data.family;
  const items = debts.data.items;
  const sections = outsideSections(items, baseCurrency);
  // A debt between two members is two rows; when both are visible, show the lender's side once.
  const once = withoutDuplicatePairs(items);
  const internal = once.filter((d) => d.internal && !d.settled);
  const settled = once.filter((d) => d.settled);
  const upcoming = upcomingInstallments(items);
  const today = todayIn(me.data.family.timezone);
  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title={t('debts.title')}
        back="/"
        action={
          <Link to="/debts/new" className={buttonVariants({ variant: 'small' })}>
            {t('debts.add')}
          </Link>
        }
      />
      <Card className="flex flex-col gap-3">
        <div className="grid grid-cols-2 gap-2">
          <TotalsTile label={t('debts.weOwe')} totals={debts.data.totals.weOwe} />
          <TotalsTile label={t('debts.owedToUs')} totals={debts.data.totals.owedToUs} />
        </div>
        <p className="text-xs text-muted">{t('debts.totalsOutside')}</p>
      </Card>
      <ListGroup>
        <li>
          <Link to="/people" className={listRowClass}>
            <IconSquare>
              <Users className="size-5" strokeWidth={2} />
            </IconSquare>
            <span className="flex-1 text-[15px] font-medium">{t('debts.people')}</span>
            <ChevronRight aria-hidden="true" className="size-5 text-muted rtl:rotate-180" />
          </Link>
        </li>
      </ListGroup>
      {items.length === 0 ? (
        <Card className="flex flex-col gap-4">
          <p className="text-muted">{t('debts.empty')}</p>
          <Link to="/debts/new" className={buttonVariants()}>
            {t('debts.add')}
          </Link>
        </Card>
      ) : (
        <>
          {upcoming.length > 0 && (
            <section className="flex flex-col gap-2">
              <SectionLabel>{t('debts.upcoming')}</SectionLabel>
              <ListGroup>
                {upcoming.map((d) => (
                  <li key={d.id}>
                    <InstallmentRow debt={d} today={today} />
                  </li>
                ))}
              </ListGroup>
            </section>
          )}
          <DirectionSection
            title={t('debts.weOwe')}
            groups={[
              { title: t('debts.sectionBorrowed'), section: sections.borrowed },
              { title: t('debts.sectionOnCredit'), section: sections.boughtOnCredit },
            ]}
            baseCurrency={baseCurrency}
            today={today}
          />
          <DirectionSection
            title={t('debts.owedToUs')}
            groups={[
              { title: t('debts.sectionLent'), section: sections.lent },
              { title: t('debts.sectionOnCredit'), section: sections.soldOnCredit },
            ]}
            baseCurrency={baseCurrency}
            today={today}
          />
          {internal.length > 0 && (
            <Section
              title={t('debts.withinFamily')}
              hint={t('debts.withinFamilyHint')}
              debts={internal}
              baseCurrency={baseCurrency}
            />
          )}
          {settled.length > 0 && (
            <Section title={t('debts.settled')} debts={settled} baseCurrency={baseCurrency} />
          )}
        </>
      )}
    </div>
  );
}
